import type { List } from 'immutable'
import type { ReactElement } from 'react'
import { SmartInputFieldRaw as SmartInputField } from '@epages/react-components/build/form/SmartInputField'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import cc from 'classcat'
import pDebounce from 'p-debounce'

import { getSearchSuggestions } from '../../../../../store/actions'
import FormGroup from '../../../../formComponents/FormGroup'
import FormValue from '../../../../formComponents/FormValue'
import ProductListEntry from './ProductListEntry'
import createPromiseLinearizer from '../../../../../utils/createPromiseLinearizer'

const maximumNumberOfAssignedProducts = 12
const suggestionDisabled = (selectedProductIds: List<string>) => (suggestion: Frontend.Product) =>
  selectedProductIds.size >= maximumNumberOfAssignedProducts || selectedProductIds.includes(suggestion.productId)
const convertSuggestionToValue = (suggestion: Frontend.Product) => suggestion.productId

type Props = {
  getProductData: (pluginId: string, pluginIds: string[]) => void
  config: WorkspacePluginProps['config']
  data: ImmutableMap
  selectedProducts: ImmutableList
} & TranslateProps

export function ProductSliderProductsSettings({
  t,
  config,
  data,
  selectedProducts,
  getProductData,
}: Readonly<Props>): ReactElement {
  const hashedThemeAssets = useSelector<State, ImmutableMap>((state) => state.getIn(['view', 'hashedThemeAssets']))
  const dispatch = useDispatch()

  const sendSearchLinearizer = createPromiseLinearizer()

  const [displayProductAssignedNotification, setDisplayProductAssignedNotification] = useState(false)
  const getStatusMessage = (status: 'success' | 'empty' | 'count' | 'full') => {
    switch (status) {
      case 'success':
        return t('footer.productAssigned')
      case 'empty':
        return t('footer.assignProductsNotification', {
          maximumNumberOfAssignedProducts,
        })
      case 'count':
        return t('footer.assignedProducts', {
          maximumNumberOfAssignedProducts,
          numberOfAssignedProducts: selectedProducts.size,
        })
      case 'full':
        return t('footer.assignProductsMaximum', {
          maximumNumberOfAssignedProducts,
        })
      default:
        return ''
    }
  }

  let status: 'success' | 'empty' | 'full' | 'count'
  if (displayProductAssignedNotification) status = 'success'
  else if (selectedProducts.size === 0) status = 'empty'
  else if (selectedProducts.size >= maximumNumberOfAssignedProducts) status = 'full'
  else status = 'count'

  const timer = useRef<number>(0)
  useEffect(() => {
    const currentTimer = timer.current
    return () => {
      if (currentTimer) window.clearTimeout(currentTimer)
    }
  }, [])
  const handleChange = (setProductIds: (ids: List<string>) => void) => (nextValue: List<string>) => {
    getProductData(data.get('id'), nextValue.toJS())
    setProductIds(nextValue)

    setDisplayProductAssignedNotification(true)
    // if there's a timer running while a new notification is displayed then
    // prevent this old timer from hiding the new notification (too soon / instantly)
    if (timer.current) window.clearTimeout(timer.current)
    // start new cancelable timer
    timer.current = window.setTimeout(() => setDisplayProductAssignedNotification(false), 3000)
  }
  const sendSearch = pDebounce<[query: string], Promise<Frontend.Product[]>>(
    (query: string) => sendSearchLinearizer(async () => await dispatch(getSearchSuggestions(query))),
    200,
  )

  const renderSuggestion = (suggestion: Frontend.Product) => {
    return (
      <div className="dali-plugin-productslider-wrapper">
        <div
          className="dali-plugin-productslider-image"
          style={{
            backgroundImage:
              suggestion.image && suggestion.image.url
                ? `url("${config?.thumbnailUrl?.(suggestion.image.url)}")`
                : `url('/themes/epages.base/assets/images/${hashedThemeAssets.getIn([
                    'epages.base/assets/images',
                    'placeholder_180.jpg',
                  ])}`,
          }}
        />
        <div className="dali-plugin-productslider-name">{suggestion.name}</div>
      </div>
    )
  }

  const getSuggestions = async (query: string, selectedProductIds: List<string>) => {
    if (selectedProductIds.size >= maximumNumberOfAssignedProducts) {
      return null
    } else {
      const products = await sendSearch(query)
      return products.filter((suggestion) => !selectedProductIds.includes(suggestion.productId))
    }
  }
  const onDelete = (productIds: List<string>, setProductIds: (ids: List<string>) => void) => (productId: string) => {
    const newProductIds = productIds.filter((id) => id !== productId).toList()
    setProductIds(newProductIds)
    getProductData(data.get('id'), newProductIds.toJS())
  }

  return (
    <>
      <FormValue name="productIds">
        {(productIds: List<string>, setProductIds: (ids: List<string>) => void) => (
          <div className="dali-settingslayer-main-content">
            <FormGroup
              name="productSearch"
              tagName="label"
              label={t('searchInputField.label')}
              className={cc([{ disabled: productIds.size >= maximumNumberOfAssignedProducts }])}
            >
              <SmartInputField
                value={productIds}
                hideValues
                strict
                getSuggestions={getSuggestions}
                onChange={handleChange(setProductIds)}
                suggestionDisabled={suggestionDisabled(productIds)}
                convertSuggestionToValue={convertSuggestionToValue}
                className="ep-form-smart"
                renderSuggestion={renderSuggestion}
                disabled={productIds.size >= maximumNumberOfAssignedProducts}
                resetText={false}
                selectText={true}
              />
            </FormGroup>
            <scroll-shadow>
              <div className="dali-plugin-productslider-productslist">
                <div className="dali-plugin-productslider-productslist-headline">{t('productList.headline')}</div>
                <div className={cc(['dali-plugin-productslider-productslist-list'])}>
                  {selectedProducts.size > 0 ? (
                    selectedProducts
                      .toJS()
                      .map(
                        (product: Core.Product) =>
                          product && (
                            <ProductListEntry
                              key={product.productId}
                              product={product}
                              config={{ thumbnailUrl: config?.thumbnailUrl }}
                              onDelete={onDelete(productIds, setProductIds)}
                            />
                          ),
                      )
                  ) : (
                    <div className="dali-plugin-productslider-productslist-emptynote">{t('productList.emptyNote')}</div>
                  )}
                </div>
              </div>
            </scroll-shadow>
          </div>
        )}
      </FormValue>
      <div className={cc(['dali-settingslayer-status', `dali-settingslayer-status-${status}`])} key={status}>
        {getStatusMessage(status)}
      </div>
    </>
  )
}
